import React, { useCallback } from 'react';

import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';
import dynamic from 'next/dynamic';

import Button from '@/components/Button';
import Skeleton from '@/components/controls/Skeleton';
import SkeletonAnimatedContainer from '@/components/controls/SkeletonAnimatedContainer';
import NotificationError from '@/components/NotificationError';
import { AppleLoginButton } from '@/components/SignIn/AppleLoginButton';
import { GoogleLoginButton } from '@/components/SignIn/GoogleLoginButton';
import Svg from '@/components/Svg';
import { TextField } from '@/components/TextField';
import { useAppleLogin } from '@/hooks/useAppleLogin';
import { useFacebookLogin } from '@/hooks/useFacebookLogin';
import { signInSchema, SignInFormData } from '@/schemas/signInSchema';

import styles from './SignIn.module.scss';

const FacebookLogin = dynamic(() => import('react-facebook-login'), {
  ssr: false,
  loading: () => (
    <SkeletonAnimatedContainer className={`${styles.socialButton}`}>
      <Skeleton className={styles.facebookSkeletonItem} />
      <Skeleton className={styles.facebookSkeletonItem} />
      <Skeleton className={styles.facebookSkeletonItem} />
    </SkeletonAnimatedContainer>
  ),
});

interface SignInProps {
  onSignInSubmit: (_data: SignInFormData) => Promise<void>;
  onSocialLogin: (_formFieldValues: any) => Promise<void>;
  onLoginByReservationId: () => void;
  showErrorBox: boolean;
  errorMsg: string[];
  settingData: {
    appleClientId?: string;
    googleAppId?: string;
    facebookId?: string;
  };
  resourcesData: Record<string, any>;
  redirectURL: string;
  isLoginByResIdEnabled?: boolean;
  appUiData: Record<string, any>;
  className?: string;
  setServerErrorMsg?: (_errors: string[]) => void;
  claimBookingCredentials?: SignInFormData;
}

const defaultStrings = {
  googleBtn: 'Continue with Google',
  facebookBtn: 'Continue with Facebook',
  appleBtn: 'Continue with Apple',
  continueWithGoogle: 'Continue with Google',
  continueWithFacebook: 'Continue with Facebook',
  continueWithApple: 'Continue with Apple',
  signUpWithSocialHeading: 'Sign up with Social',
  logInWithSocialHeading: 'Login with Social',
  email: 'Email',
  pswd: 'Password',
  signinButton: 'Signin',
  loginButton: 'Login',
  forgotPwd: 'Forgot your password?',
  DontHaveAccount: "Don't have an account?",
  signUP: 'Sign Up',
  serverErrorMsg:
    "Looks like you've entered an incorrect email address and/or password (we get it, there are too many to remember these days). Please try again.",
  loginWithReservationButton: 'Login with booking Reference',
};

export const SignIn: React.FC<SignInProps> = ({
  onSignInSubmit,
  onSocialLogin,
  onLoginByReservationId,
  showErrorBox,
  errorMsg,
  settingData,
  resourcesData,
  redirectURL,
  isLoginByResIdEnabled,
  appUiData,
  className,
  setServerErrorMsg,
  claimBookingCredentials,
}) => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: claimBookingCredentials?.email || '',
      password: claimBookingCredentials?.password || '',
    },
    mode: 'all',
  });

  const resourceString = resourcesData && Object.keys(resourcesData).length > 0 ? resourcesData : defaultStrings;

  const { appleClientId, googleAppId, facebookId } = settingData;

  // Initialize social login hooks
  const { handleFacebookLogin } = useFacebookLogin({
    facebookId,
    onLogin: (data) => {
      setServerErrorMsg?.([]);
      return onSocialLogin(data);
    },
    formState: { email: '', password: '' },
  });

  const { handleAppleLogin } = useAppleLogin({
    onLogin: (data) => {
      setServerErrorMsg?.([]);
      return onSocialLogin(data);
    },
    formState: { email: '', password: '' },
  });

  const handleFormSubmit = useCallback(
    async (data: SignInFormData) => {
      try {
        setServerErrorMsg?.([]);
        await onSignInSubmit(data);
      } catch (error) {
        console.error('Error during sign-in:', error);
        setServerErrorMsg?.(['An error occurred during sign in. Please try again.']);
      }
    },
    [onSignInSubmit, setServerErrorMsg],
  );

  return (
    <div className={classNames(styles.signInContainer, className)}>
      <div>
        {showErrorBox && (
          <NotificationError
            mainText="This page contains errors 🤦"
            subText={errorMsg.length > 0 ? errorMsg : resourceString.serverErrorMsg}
            icon
          />
        )}
        <form onSubmit={handleSubmit(handleFormSubmit)} className={styles.formContainer} name="signInForm" noValidate>
          <TextField
            className={styles.inputWrapper}
            name="email"
            control={control}
            type="email"
            required
            label={resourceString.email}
          />
          <TextField
            className={styles.inputWrapper}
            name="password"
            control={control}
            type="password"
            required
            label={resourceString.pswd}
            showPasswordToggle
          />
          <div className={styles.signInButtonContainer}>
            <Button type="submit" variant="primary" className="submitButton" disabled={isSubmitting}>
              {resourceString.loginButton}
            </Button>
          </div>
        </form>
        {isLoginByResIdEnabled && (
          <div className={styles.signInButtonContainer}>
            <Button
              type="button"
              variant="secondary"
              classWrapper="bookingReferenceContainer"
              className="submitButton"
              onClick={onLoginByReservationId}
            >
              {appUiData?.loginWithReservationButton || defaultStrings.loginWithReservationButton}
            </Button>
          </div>
        )}

        <div className={styles.socialLoginContainer}>
          <div className={styles.loginWithSocialHeading}>
            {resourceString.logInWithSocialHeading || defaultStrings.logInWithSocialHeading}
          </div>
          <div className={styles.socialButtonsList}>
            <div>
              {googleAppId && (
                <GoogleLoginButton
                  formState={{ email: '', password: '' }}
                  onLogin={onSocialLogin}
                  ariaLabel={resourceString.continueWithGoogle || defaultStrings.googleBtn}
                >
                  {resourceString.continueWithGoogle || defaultStrings.googleBtn}
                </GoogleLoginButton>
              )}
            </div>
            <div id="facebookButton">
              {facebookId && (
                <FacebookLogin
                  appId={facebookId}
                  autoLoad={false}
                  redirectUri={redirectURL}
                  fields="first_name, last_name, gender, picture, email, birthday"
                  callback={handleFacebookLogin}
                  onFailure={() => {}}
                  // cssClass={`${styles.facebookButtonContainer}`}
                  cssClass={`${styles.socialButton} ${styles.facebookButton} socialButton facebookButton`}
                  isMobile={false}
                  scope="public_profile, email, user_birthday"
                  textButton={resourceString.continueWithFacebook || defaultStrings.facebookBtn}
                  icon={
                    <Svg
                      glyph="facebook"
                      fill="#fff"
                      width={26}
                      height={26}
                      viewBoxX={3}
                      viewBoxY={3}
                      ariaLabel="facebook"
                      iconId="facebook-new-btn"
                    />
                  }
                />
              )}
            </div>
            <div className={styles.appleLoginButton}>
              {appleClientId && (
                <AppleLoginButton
                  clientId={appleClientId}
                  handleAppleLogin={handleAppleLogin}
                  redirectURI={redirectURL}
                  ariaLabel={resourceString.continueWithApple || defaultStrings.appleBtn}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
